import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { Subject, switchMap } from 'rxjs';
import { WarningBoxComponent } from '../../../account/components/warning-box/warning-box.component';
import { billingScopes } from '../../../billing-consts';
import { ColorPickerComponent } from '../../../color-picker/color-picker.component';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { GalleryEmbedService, NewGalleryEmbedOptions } from '../../../services/gallery-embed.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { ShareDirective } from '../../../shared-extra/directives/share.directive';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ClickTrackDirective } from '../../../shared/directives/click-track.directive';
import { ShareGalleryDialogComponent } from '../share-gallery-dialog/share-gallery-dialog.component';

@Component({
  selector: 'app-embed-gallery-dialog',
  imports: [
    ButtonDirective,
    SvgComponent,
    ContextMenuComponent,
    SwitchComponent,
    ColorPickerComponent,
    FormsModule,
    ClickTrackDirective,
    BillingScopePipe,
    UpgradeBadgeComponent,
    ShareDirective,
    WarningBoxComponent,
    LoadingIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './embed-gallery-dialog.component.html',
  styleUrl: './embed-gallery-dialog.component.scss',
})
export class EmbedGalleryDialogComponent implements OnGetData {
  dialogName = 'embed-gallery';
  dialogOptions(): DialogOptions {
    return {
      size: 560,
      spacingX: 24,
      spacingY: 24,
      hideClose: true,
    };
  }

  private galleryService = inject(GalleryService);
  private dialog = inject(DialogService);
  private galleryEmbedService = inject(GalleryEmbedService);
  private toasts = inject(ToastService);
  private state = inject(StateService);
  private permissionService = inject(PermissionService);

  private galleryId = new Subject<string>();
  gallery = toSignal(this.galleryId.pipe(switchMap(galleryId => this.galleryService.getGallery(galleryId))));
  newOptions = signal<NewGalleryEmbedOptions>(this.galleryEmbedService.getNewEmbedPreferences());
  activeTenant = toSignal(this.state.activeTenant$);

  embedCode = computed(() => {
    const gallery = this.gallery();
    const options = this.newOptions();
    if (!options || !gallery) return '';

    const code = this.galleryEmbedService.getNewEmbedCode(gallery, options);
    if (!this.permissionService.canDisplayElement(billingScopes.gallery.embedGallery)) {
      return code.substring(0, 395) + '...';
    }
    return code;
  });

  onGetData(data: { gallery: GalleryDto }) {
    this.galleryId.next(data.gallery.id);
  }

  onEmbedChange() {
    this.newOptions.update(options => ({ ...options }));
  }

  onBack() {
    this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery() });
  }

  manageLink() {
    this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery(), mode: 'link' });
  }

  onCopy() {
    this.toasts.add('Embed code copied!', 'success');
  }
}
